/**
 * ContactActivitiesSection - Seção de atividades pendentes do contato
 * Exibe atividades não concluídas de negócios e clientes vinculados
 * 
 * Características:
 * - Card colapsável estilo premium
 * - Lazy loading (só busca quando abre)
 * - Lista de atividades com ícone, título, negócio e data agendada
 * - Botão para marcar como concluída
 * 
 * RELEASE: 2026-01-14
 */

import { useState, useMemo, useCallback } from 'react';
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  ChevronDown, 
  ChevronRight, 
  CheckCircle, 
  Loader2,
  FileText,
  CheckSquare,
  Phone,
  Mail,
  Users,
  Calendar,
  Briefcase
} from 'lucide-react';
import { 
  useContactPendingActivities, 
  getActivityTypeConfig,
  type ContactPendingActivity,
  type ActivityType
} from '@/hooks/useContactPendingActivities';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { format, isPast, isToday, isTomorrow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

// ============================================================================
// PROPS
// ============================================================================

interface ContactActivitiesSectionProps {
  /** ID do contato para buscar atividades */
  contactId: string;
  /** Callback para atualizar dados do componente pai */
  onRefresh?: () => void;
  /** Contagem inicial vinda da RPC de contadores (exibe antes de expandir) */
  initialCount?: number;
}

// ============================================================================
// ÍCONES POR TIPO
// ============================================================================

/**
 * Retorna o componente de ícone baseado no tipo de atividade
 */
function getActivityIcon(type: ActivityType) {
  const iconMap: Record<string, React.ReactNode> = {
    note: <FileText className="h-2.5 w-2.5" />,
    task: <CheckSquare className="h-2.5 w-2.5" />,
    call: <Phone className="h-2.5 w-2.5" />,
    email: <Mail className="h-2.5 w-2.5" />,
    meeting: <Users className="h-2.5 w-2.5" />
  };
  return iconMap[type] || <FileText className="h-2.5 w-2.5" />;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Formata a data agendada de forma amigável
 */
function formatScheduledDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  
  try {
    const date = parseISO(dateStr);
    
    if (isToday(date)) {
      return `Hoje, ${format(date, 'HH:mm', { locale: ptBR })}`;
    }
    
    if (isTomorrow(date)) {
      return `Amanhã, ${format(date, 'HH:mm', { locale: ptBR })}`;
    }
    
    if (isPast(date)) {
      return `Atrasada - ${format(date, 'dd/MM', { locale: ptBR })}`;
    }
    
    return format(date, "dd/MM 'às' HH:mm", { locale: ptBR });
  } catch {
    return null;
  }
}

/**
 * Card individual de atividade
 */
function ActivityCard({ 
  activity, 
  onComplete, 
  onClick,
  isCompleting
}: { 
  activity: ContactPendingActivity; 
  onComplete: (id: string) => void;
  onClick: () => void;
  isCompleting: boolean;
}) {
  const config = getActivityTypeConfig(activity.activity_type);
  const scheduledText = formatScheduledDate(activity.scheduled_at);
  const isOverdue = activity.scheduled_at ? isPast(parseISO(activity.scheduled_at)) : false;

  // Truncar título e nome do negócio - limites ajustados para caber no card
  const truncatedTitle = activity.title.length > 28 
    ? `${activity.title.substring(0, 28)}...` 
    : activity.title;
  const truncatedDealTitle = activity.deal?.title 
    ? (activity.deal.title.length > 18 ? `${activity.deal.title.substring(0, 18)}...` : activity.deal.title)
    : null;

  return (
    <div 
      className={cn(
        "flex items-start gap-1.5 p-1.5 rounded-lg border bg-background/50",
        "hover:bg-muted/30 transition-colors cursor-pointer",
        isOverdue && "border-destructive/30"
      )}
      onClick={onClick}
    >
      {/* Ícone do tipo */}
      <div className={cn(
        "flex items-center justify-center w-5 h-5 rounded-md shrink-0",
        config.colorClass
      )}>
        {getActivityIcon(activity.activity_type)}
      </div>

      {/* Conteúdo */}
      <div className="flex-1 min-w-0 space-y-0">
        {/* Título */}
        <p className="text-[10px] font-medium text-foreground truncate leading-tight">
          {truncatedTitle}
        </p>

        {/* Negócio vinculado */}
        {truncatedDealTitle && (
          <div className="flex items-center gap-0.5 text-[8px] text-muted-foreground">
            <Briefcase className="h-2 w-2" />
            <span className="truncate">{truncatedDealTitle}</span>
          </div>
        )}

        {/* Data agendada */}
        {scheduledText && (
          <div className={cn(
            "flex items-center gap-0.5 text-[8px]",
            isOverdue ? "text-destructive" : "text-muted-foreground"
          )}>
            <Calendar className="h-2 w-2" />
            <span>{scheduledText}</span>
          </div>
        )}
      </div>

      {/* Botão de concluir */}
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0 text-muted-foreground hover:text-primary"
        onClick={(e) => {
          e.stopPropagation();
          onComplete(activity.id);
        }}
        disabled={isCompleting}
      >
        {isCompleting ? (
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
        ) : (
          <CheckCircle className="h-2.5 w-2.5" />
        )}
      </Button>
    </div>
  );
}

/**
 * Skeleton de loading para atividades
 */
function ActivitiesSkeleton() {
  return (
    <div className="space-y-1.5 px-4 pb-4">
      {[1, 2].map((i) => (
        <div key={i} className="flex items-start gap-1.5 p-1.5 rounded-lg border">
          <Skeleton className="w-5 h-5 rounded-md" />
          <div className="flex-1 space-y-0.5">
            <Skeleton className="h-2.5 w-3/4" />
            <Skeleton className="h-2 w-1/2" />
          </div>
          <Skeleton className="w-5 h-5 rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Estado vazio quando não há atividades
 */
function EmptyState() {
  return (
    <div className="px-4 pb-4">
      <div className="flex items-center gap-2 text-muted-foreground text-sm py-3">
        <CheckCircle className="h-4 w-4 text-primary" />
        <span>Nenhuma atividade pendente</span>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ContactActivitiesSection({ 
  contactId,
  onRefresh,
  initialCount
}: ContactActivitiesSectionProps) {
  // Estado de abertura do collapsible
  const [isOpen, setIsOpen] = useState(false);
  
  // Estado para modal de detalhes do negócio
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  
  // Estado para controlar qual atividade está sendo completada
  const [completingId, setCompletingId] = useState<string | null>(null);

  // Hook para buscar atividades (lazy loading)
  const { 
    activities, 
    isLoading, 
    completeActivity 
  } = useContactPendingActivities(contactId, { 
    enabled: isOpen,
    limit: 20
  });

  // Handler para completar atividade
  const handleComplete = useCallback(async (activityId: string) => {
    setCompletingId(activityId);
    try {
      await completeActivity.mutateAsync(activityId);
      onRefresh?.();
    } finally {
      setCompletingId(null);
    }
  }, [completeActivity, onRefresh]);

  // Handler para abrir modal do negócio
  const handleActivityClick = useCallback((activity: ContactPendingActivity) => {
    if (activity.deal_id) {
      setSelectedDealId(activity.deal_id);
    }
  }, []);

  // Ordenar atividades: atrasadas primeiro, depois por data
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      // Se ambas têm data agendada, ordenar por data
      if (a.scheduled_at && b.scheduled_at) {
        return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
      }
      // Se só uma tem data, ela vem primeiro
      if (a.scheduled_at) return -1;
      if (b.scheduled_at) return 1;
      // Se nenhuma tem data, ordenar por criação
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [activities]);

  // Contador de atividades atrasadas
  const overdueCount = useMemo(() => {
    return activities.filter(a => a.scheduled_at && isPast(parseISO(a.scheduled_at))).length;
  }, [activities]);

  return (
    <>
      {/* Card colapsável de atividades */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
          {/* Header/Trigger */}
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">
                  Atividades
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Badge de contador — sempre reflete o total real (initialCount), mesmo após expandir */}
                {((isOpen && initialCount != null ? initialCount : (isOpen ? activities.length : (initialCount ?? 0))) > 0) && (
                  <Badge 
                    variant={isOpen && overdueCount > 0 ? "destructive" : "secondary"} 
                    className="rounded-full text-xs"
                  >
                    {isOpen && initialCount != null ? initialCount : (isOpen ? activities.length : initialCount)}
                    {isOpen && overdueCount > 0 && ` (${overdueCount} atrasada${overdueCount > 1 ? 's' : ''})`}
                  </Badge>
                )}
                
                {/* Ícone de seta */}
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </button>
          </CollapsibleTrigger>

          {/* Conteúdo */}
          <CollapsibleContent>
            {isLoading ? (
              <ActivitiesSkeleton />
            ) : sortedActivities.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="px-4 pb-4 space-y-2">
                {sortedActivities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    onComplete={handleComplete}
                    onClick={() => handleActivityClick(activity)}
                    isCompleting={completingId === activity.id}
                  />
                ))}

                {/* Rodapé: avisa quando existem atividades não exibidas pelo limite */}
                {initialCount != null && activities.length < initialCount && (
                  <div className="text-center py-1.5">
                    <span className="text-[10px] text-muted-foreground/80">
                      +{initialCount - activities.length} atividade{initialCount - activities.length > 1 ? 's' : ''} não exibida{initialCount - activities.length > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>

      {/* Modal de detalhes do negócio */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={!!selectedDealId}
        onOpenChange={(open) => !open && setSelectedDealId(null)}
      />
    </>
  );
}
